import { http } from '@/plugins/http'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_SETS,
  RECEIVING_SETS,
  RECEIVE_SET_METADATA,
  RECEIVE_SET_FAILURE,
  SAVING_SET,
  SAVING_SET_FAILURE,
  UPDATING_SET,
  UPDATING_SET_FAILURE,
  DELETE_SET,
  DELETING_SET,
  DELETING_SET_FAILURE,
  CLEAR_SET_STATE,
  RESET_SET_META,
  CLEAR_STATE,
  INCREMENT_SET_COUNT
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrl, buildUrlWithQuery } from '@/utils/api'
import handleXHRError from '@/utils/helpers/HandleXHRError'
import {
  receiveData,
  deleteData,
  increment,
  receiveMetadata,
  resetMetadata,
  clearLocalState,
  clearState
} from '@/store/modules/helpers/mutationHelpers'

const setSchema = new schema.Entity('sets')
const setListSchema = new schema.Array(setSchema)

const state = {
  data: {},
  cache: {},
  count: null,
  shown: null,
  next: null,
  previous: null,
  currentPage: 1,
  saving: false,
  saveFailure: null,
  receivingData: false,
  receiveDataFailure: false,
  updating: null,
  updateFailure: null,
  deleting: false,
  deleteFailure: null
}

const actions = {
  fetchSetsWithQuery (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_SETS, true)
    return http.get(buildUrlWithQuery('sets', query, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_SETS, false)
        this.commit(RESET_SET_META)
        if (!r.hasOwnProperty('results')) return this.commit(RECEIVE_SET_FAILURE, 'Error fetching sets')
        const { results, next, previous, count } = r
        const { entities, result } = normalize(results, setListSchema)
        this.commit(RECEIVE_SETS, { data: entities.sets })
        this.commit(RECEIVE_SET_METADATA, { next, previous, count, result: result.length })
        return { query, count, entities }
      })
      .catch(e => {
        this.commit(RECEIVING_SETS, false)
        this.commit(RECEIVE_SET_FAILURE, handleXHRError(e))
      })
  },
  fetchSetOptions (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_SETS, true)
    return http({ url: buildUrl('sets', query || null, instanceUrl), headers, method: 'options' })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_SETS, false)
        if (r.hasOwnProperty('choices')) {
          const { choices } = r
          const { entities } = normalize(choices, setListSchema)
          this.commit(RECEIVE_SETS, { data: entities.sets })
          return r
        }
      })
      .catch(e => {
        this.commit(RECEIVING_SETS, false)
        this.commit(RECEIVE_SET_FAILURE, handleXHRError(e))
      })
  }
}

const getters = {
  getSets: (state) => {
    return state.data ? Object.values(state.data).map(v => Object.assign({}, v)) : []
  },
  getAllSets: (state) => {
    return state.cache ? Object.values(state.cache).map(v => Object.assign({}, v)) : []
  },
  getSetMetadata: (state) => {
    const { shown, count } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown }
  },
  getSetById: (state) => (id) => {
    if (state.data && state.data[id]) {
      return state.data[id]
    } else if (state.cache && state.cache[id]) {
      return state.cache[id]
    } else {
      return {}
    }
  },
  getDefaultSet: (state) => {
    return state.cache ? Object.values(state.cache).find(v => v.active) : {}
  },
  getSetFetchStatus: (state) => {
    return state.receivingData
  },
  getSetSaveStatus: (state) => {
    return state.saving
  },
  getSetUpdateStatus: (state) => {
    return state.updating
  },
  getSetDeleteStatus: (state) => {
    return state.deleting
  }
}

const mutations = {
  [RECEIVE_SETS] (state, results) {
    receiveData(state, results)
  },
  [RECEIVING_SETS] (state, payload) {
    state.receivingData = payload
  },
  [RECEIVE_SET_FAILURE] (state, error) {
    state.receiveDataFailure = error
  },
  [SAVING_SET] (state, payload) {
    state.saving = payload
  },
  [SAVING_SET_FAILURE] (state, payload) {
    state.saveFailure = payload
  },
  [UPDATING_SET] (state, payload) {
    state.updating = payload
  },
  [UPDATING_SET_FAILURE] (state, payload) {
    const { id, err } = payload
    state.updateFailure = { id, err }
  },
  [DELETE_SET] (state, payload) {
    deleteData(state, payload)
  },
  [DELETING_SET] (state, payload) {
    state.deleting = payload
  },
  [DELETING_SET_FAILURE] (state, payload) {
    const { id, err } = payload
    state.deleteFailure = { id, err }
  },
  [INCREMENT_SET_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVE_SET_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [CLEAR_SET_STATE] (state, payload) {
    clearLocalState(state, payload)
  },
  [RESET_SET_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    clearState(state)
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}
